import React, { useState, useEffect, useRef } from 'react';
import './PresentationViewer.css';

const PresentationViewer = ({ presentation, onReset }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const utteranceRef = useRef(null);
  const presentationRef = useRef(null);
  const isIntentionallyStoppingRef = useRef(false);
  const remainingTextRef = useRef(null); // Store remaining text when paused
  const currentSlideNotesRef = useRef(null); // Store current slide's speaker notes
  const isFirefoxRef = useRef(false); // Detect Firefox for compatibility fixes
  const pauseTimeRef = useRef(null); // Track when we paused (for Firefox fallback)
  const startTimeRef = useRef(null); // Track when narration started (for Firefox fallback)
  
  // Detect Firefox on component mount
  useEffect(() => {
    isFirefoxRef.current = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  }, []);

  const currentSlide = presentation.slides[currentSlideIndex];
  const totalSlides = presentation.slides.length;

  useEffect(() => {
    // Auto-start presentation
    if (presentation && presentation.slides.length > 0) {
      startPresentation();
    }

    return () => {
      stopNarration();
    };
  }, []);

  useEffect(() => {
    if (isPlaying && currentSlide) {
      // Small delay for Firefox to ensure state is ready
      const delay = isFirefoxRef.current ? 100 : 0;
      setTimeout(() => {
        narrateSlide(currentSlide);
      }, delay);
    }
  }, [currentSlideIndex, isPlaying]);

  const startPresentation = () => {
    setIsPlaying(true);
    setCurrentSlideIndex(0);
  };

  const narrateSlide = (slide) => {
    if (!slide.speakerNotes) {
      // If no speaker notes, advance after a short delay
      setTimeout(() => {
        advanceSlide();
      }, 2000);
      return;
    }

    // Store current slide's notes
    const currentNotes = slide.speakerNotes;
    currentSlideNotesRef.current = currentNotes;
    
    // If the remaining text doesn't match the current slide, it's from a previous slide
    // Clear it and start from the beginning
    if (remainingTextRef.current && 
        !currentNotes.includes(remainingTextRef.current) && 
        !remainingTextRef.current.includes(currentNotes)) {
      remainingTextRef.current = null;
    }

    // Use Web Speech API for TTS (free, no license required)
    if ('speechSynthesis' in window) {
      // If we have remaining text from a pause, use that; otherwise use full text
      const textToSpeak = remainingTextRef.current || currentNotes;
      
      // Don't clear remainingTextRef yet - we'll update it as we speak via onboundary
      // Reset flag before starting new narration
      isIntentionallyStoppingRef.current = false;

      const utterance = new SpeechSynthesisUtterance(textToSpeak);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;

      // Track position using boundary events (fires on word/sentence boundaries)
      utterance.onboundary = (event) => {
        // Firefox may have issues with charIndex, so we handle it carefully
        if (event.charIndex !== undefined && event.charIndex !== null) {
          const currentPosition = event.charIndex + (event.charLength || 0);
          if (currentPosition < textToSpeak.length) {
            const remaining = textToSpeak.substring(currentPosition).trim();
            // Only update if we have meaningful remaining text
            if (remaining.length > 0) {
              remainingTextRef.current = remaining;
            }
          } else {
            // We've reached the end
            remainingTextRef.current = null;
          }
        }
      };
      
      // Track start time for Firefox fallback
      utterance.onstart = () => {
        startTimeRef.current = Date.now();
        pauseTimeRef.current = null;
      };

      utterance.onend = () => {
        // In Firefox, onend may fire even when we cancel, so we need extra checks
        if (isIntentionallyStoppingRef.current) {
          // Don't advance if we're intentionally stopping
          return;
        }
        
        // Clear remaining text when narration completes naturally
        remainingTextRef.current = null;
        currentSlideNotesRef.current = null;
        startTimeRef.current = null;
        
        // Advance to next slide
        advanceSlide();
      };

      utterance.onerror = (event) => {
        // Ignore 'interrupted' errors - they're expected when pausing/canceling
        if (event.error === 'interrupted') {
          // This is expected when stopping narration, so we ignore it
          return;
        }
        
        // Log other errors (network issues, voice unavailable, etc.)
        console.warn('Speech synthesis error:', event.error, event);
        
        // Only advance on real errors, not intentional interruptions
        if (!isIntentionallyStoppingRef.current) {
          // Fallback: advance after a delay for real errors
          setTimeout(() => {
            advanceSlide();
          }, 3000);
        }
      };

      utteranceRef.current = utterance;
      
      // For Firefox, we need to ensure speechSynthesis is ready
      if (isFirefoxRef.current) {
        // Firefox sometimes needs a small delay
        setTimeout(() => {
          window.speechSynthesis.speak(utterance);
        }, 50);
      } else {
        window.speechSynthesis.speak(utterance);
      }
    } else {
      // Fallback: show notes and allow manual advance
      setTimeout(() => {
        advanceSlide();
      }, 5000);
    }
  };

  const pauseNarration = () => {
    // Set flag to indicate we're intentionally stopping
    isIntentionallyStoppingRef.current = true;
    
    // Track pause time for Firefox fallback
    pauseTimeRef.current = Date.now();
    
    if (window.speechSynthesis && utteranceRef.current) {
      // For Firefox, we need to be more careful with pause/cancel
      if (isFirefoxRef.current) {
        // Firefox: cancel immediately and rely on boundary tracking
        // If boundary tracking didn't work, we'll use word-based fallback
        window.speechSynthesis.cancel();
      } else {
        // Chrome/Edge: try pause first, then cancel
        if (window.speechSynthesis.pause) {
          window.speechSynthesis.pause();
        }
        window.speechSynthesis.cancel();
      }
    }
    
    // Firefox fallback: if boundary tracking didn't work, try word-based approach
    if (isFirefoxRef.current && !remainingTextRef.current && currentSlideNotesRef.current) {
      // Try to estimate position based on time elapsed
      // This is a rough estimate, but better than restarting from beginning
      if (startTimeRef.current && pauseTimeRef.current) {
        const elapsed = pauseTimeRef.current - startTimeRef.current;
        // Rough estimate: ~150 words per minute = ~2.5 words per second
        // Average word length ~5 characters, so ~12.5 chars per second
        const estimatedChars = Math.floor((elapsed / 1000) * 12.5);
        if (estimatedChars > 0 && estimatedChars < currentSlideNotesRef.current.length) {
          remainingTextRef.current = currentSlideNotesRef.current.substring(estimatedChars).trim();
        }
      }
      
      // If time-based didn't work, try keeping the full text as fallback
      if (!remainingTextRef.current) {
        remainingTextRef.current = currentSlideNotesRef.current;
      }
    } else if (!remainingTextRef.current && utteranceRef.current && currentSlideNotesRef.current) {
      // Fallback for other browsers: if boundary tracking didn't work
      remainingTextRef.current = currentSlideNotesRef.current;
    }
    
    utteranceRef.current = null;
    
    // Reset flag after a delay to allow error handlers to check it
    // Firefox needs a longer delay to prevent onend from firing
    const delay = isFirefoxRef.current ? 300 : 100;
    setTimeout(() => {
      isIntentionallyStoppingRef.current = false;
    }, delay);
  };

  const stopNarration = () => {
    // Set flag to indicate we're intentionally stopping
    isIntentionallyStoppingRef.current = true;
    
    if (window.speechSynthesis) {
      // Cancel immediately (for full stop, not pause)
      window.speechSynthesis.cancel();
    }
    
    // Clear remaining text on full stop
    remainingTextRef.current = null;
    currentSlideNotesRef.current = null;
    utteranceRef.current = null;
    
    // Reset flag after a short delay to allow error handlers to check it
    setTimeout(() => {
      isIntentionallyStoppingRef.current = false;
    }, 100);
  };

  const advanceSlide = () => {
    if (currentSlideIndex < totalSlides - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    } else {
      // Presentation complete
      setIsPlaying(false);
    }
  };

  const goToPreviousSlide = () => {
    if (currentSlideIndex > 0) {
      // Set flag BEFORE stopping to prevent onend from advancing
      isIntentionallyStoppingRef.current = true;
      stopNarration(); // Full stop when changing slides
      
      // Use functional update to ensure we get the correct current index
      setCurrentSlideIndex(prevIndex => {
        // Double-check we're not at the first slide
        if (prevIndex > 0) {
          return prevIndex - 1;
        }
        return prevIndex;
      });
      
      // Reset flag after a delay (longer for Firefox)
      const delay = isFirefoxRef.current ? 300 : 100;
      setTimeout(() => {
        isIntentionallyStoppingRef.current = false;
      }, delay);
    }
  };

  const goToNextSlide = () => {
    if (currentSlideIndex < totalSlides - 1) {
      // Set flag BEFORE stopping to prevent onend from advancing
      isIntentionallyStoppingRef.current = true;
      stopNarration(); // Full stop when changing slides
      
      // Use functional update to ensure we get the correct current index
      setCurrentSlideIndex(prevIndex => {
        // Double-check we're not at the last slide
        if (prevIndex < totalSlides - 1) {
          return prevIndex + 1;
        }
        return prevIndex;
      });
      
      // Reset flag after a delay (longer for Firefox)
      const delay = isFirefoxRef.current ? 300 : 100;
      setTimeout(() => {
        isIntentionallyStoppingRef.current = false;
      }, delay);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (presentationRef.current.requestFullscreen) {
        presentationRef.current.requestFullscreen();
      } else if (presentationRef.current.webkitRequestFullscreen) {
        presentationRef.current.webkitRequestFullscreen();
      } else if (presentationRef.current.msRequestFullscreen) {
        presentationRef.current.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const handlePausePlay = () => {
    if (isPlaying) {
      pauseNarration(); // Use pause instead of stop to preserve position
      setIsPlaying(false);
    } else {
      // Resume from where we left off
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPreviousSlide();
      } else if (e.key === 'ArrowRight') {
        goToNextSlide();
      } else if (e.key === ' ') {
        e.preventDefault();
        handlePausePlay();
      } else if (e.key === 'Escape') {
        if (isFullscreen) {
          toggleFullscreen();
        } else {
          onReset();
        }
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlideIndex, isPlaying, isFullscreen]);

  if (!currentSlide) {
    return null;
  }

  return (
    <div className="presentation-viewer" ref={presentationRef}>
      <div className="presentation-header">
        <div className="presentation-title">{presentation.topic}</div>
        <div className="slide-counter">
          {currentSlideIndex + 1} / {totalSlides}
        </div>
      </div>

      <div className="slide-container">
        <div 
          className={`slide-content ${currentSlide.imageUrl ? `slide-with-image slide-image-${currentSlide.imagePosition || 'top'}` : ''}`}
        >
          <div className="slide-main-content">
            {currentSlide.imageUrl && (currentSlide.imagePosition === 'left') && (
              <div className={`slide-image-side slide-image-${currentSlide.imagePosition}`}>
                <div className="slide-image-gradient-top"></div>
                <div className="slide-image-gradient-bottom"></div>
                <div className="slide-image-gradient-right"></div>
                <img 
                  src={currentSlide.imageUrl} 
                  alt={currentSlide.title}
                  className="slide-image"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            <div className="slide-text-content">
              <h1 className="slide-title">{currentSlide.title}</h1>
              
              {currentSlide.imageUrl && currentSlide.imagePosition === 'top' && (
                <div className="slide-image-top">
                  <img 
                    src={currentSlide.imageUrl} 
                    alt={currentSlide.title}
                    className="slide-image"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              <ul className="slide-bullets">
                {currentSlide.bullets.map((bullet, index) => (
                  <li key={index} className="slide-bullet">
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>

            {currentSlide.imageUrl && currentSlide.imagePosition === 'right' && (
              <div className="slide-image-side slide-image-right">
                <div className="slide-image-gradient-top"></div>
                <div className="slide-image-gradient-bottom"></div>
                <div className="slide-image-gradient-left"></div>
                <img 
                  src={currentSlide.imageUrl} 
                  alt={currentSlide.title}
                  className="slide-image"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {currentSlide.speakerNotes && (
          <div className="speaker-notes">
            <div className="speaker-notes-label">Speaker Notes:</div>
            <div className="speaker-notes-text">{currentSlide.speakerNotes}</div>
          </div>
        )}
      </div>

      <div className="presentation-controls">
        <button
          onClick={onReset}
          className="control-button reset-button"
          title="Exit (Esc)"
        >
          ← Exit
        </button>
        <button
          onClick={goToPreviousSlide}
          disabled={currentSlideIndex === 0}
          className="control-button"
          title="Previous (←)"
        >
          ← Previous
        </button>
        <button
          onClick={handlePausePlay}
          className="control-button play-button"
          title="Pause/Play (Space)"
        >
          {isPlaying ? '⏸ Pause' : '▶ Play'}
        </button>
        <button
          onClick={goToNextSlide}
          disabled={currentSlideIndex === totalSlides - 1}
          className="control-button"
          title="Next (→)"
        >
          Next →
        </button>
        <button
          onClick={toggleFullscreen}
          className="control-button fullscreen-button"
          title="Fullscreen (F)"
        >
          {isFullscreen ? '⤓ Exit Fullscreen' : '⤢ Fullscreen'}
        </button>
      </div>

      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${((currentSlideIndex + 1) / totalSlides) * 100}%` }}
        />
      </div>
    </div>
  );
};

export default PresentationViewer;
